import {
  Grid,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
} from "@mui/material";
import CurrencyFormField from "components/CurrencyFormField/CurrencyFormField";
import FormField from "components/FormField/FormField";
import TimeFormField from "components/TimeFormField/TimeFormField";
import currencyEndAdornment from "constants/currencyEndAdornment";
import timeEndAdornment from "constants/timeEndAdornment";
import { UpdateProposalProductInputSchema } from "DDD/action-objects/StageProductUpdate";
import { Crew, ProductLabourCostType, Proposal, ProposalCrew } from "generated/graphql";
import useAddProposalCrew from "hooks/proposals/useAddProposalCrew";
import AddLabourCost from "modules/proposals/labourCosts/AddLabourCost";
import { ProposalContext } from "modules/proposals/providers/ProposalContextProvider";
import { useCallback, useContext, useMemo } from "react";
import { useFieldArray, useFormContext } from "react-hook-form";
import formatCentsToUSD from "utils/money/formatCentsToUSD";
import StageProductContext from "../StageProductContext";
import useAddProposalCrews from "hooks/proposals/useAddProposalCrews";
import dayEndAdornment from "constants/dayEndAdornment";
import halfDayEndAdornment from "constants/halfDayEndAdornment";

function LabourCostsSubForm({ averageWorkingHoursPerDay }: { averageWorkingHoursPerDay: number }) {
  const { inViewMode } = useContext(ProposalContext);
  const [addProposalCrews, { isSuccess }] = useAddProposalCrews();
  const {
    register,
    control,
    watch,
    handleSubmit,
    formState: { isSubmitSuccessful },
  } = useFormContext<UpdateProposalProductInputSchema>();
  const { product, onSubmit } = useContext(StageProductContext);
  const { fields, remove } = useFieldArray({
    control,
    name: "crews",
    keyName: "key",
  });

  const jobDurationInDays = useMemo(() => {
    if (product.jobDurationDaysOverride) {
      return product.jobDurationDaysOverride;
    }

    const jobDuration = product?.jobDurationOverride || product.jobDuration;
    return jobDuration > 0 ? jobDuration / averageWorkingHoursPerDay : 0;
  }, [product, averageWorkingHoursPerDay]);

  const crewsById = useMemo(() => {
    return product.crews.reduce((acc, val) => {
      return {
        ...acc,
        [val.id]: val,
      };
    }, {} as Record<string, ProposalCrew | undefined>);
  }, [product.crews]);

  const getJobTimePlaceholder = useCallback(
    (index: number): string => {
      const labourCostType = product.material.labourCostType;

      switch (labourCostType) {
        case ProductLabourCostType.FULL_DAY:
          return Math.ceil(jobDurationInDays).toFixed(2);

        case ProductLabourCostType.HALF_DAY:
          return Math.ceil(jobDurationInDays * 2).toFixed(2);

        case ProductLabourCostType.PER_MAN_HOUR: {
          const existingTimeOnJob = product.crews?.[index]?.timeOnjob;
          if (existingTimeOnJob !== undefined) {
            return (existingTimeOnJob / 3600).toFixed(2);
          }
          return (jobDurationInDays * averageWorkingHoursPerDay).toFixed(2);
        }

        default: {
          const existingTimeOnJob = product.crews?.[index]?.timeOnjob;
          if (existingTimeOnJob !== undefined) {
            return (existingTimeOnJob / 3600).toFixed(2);
          }
          return jobDurationInDays.toFixed(2);
        }
      }
    },
    [product.material.labourCostType, product.crews, jobDurationInDays, averageWorkingHoursPerDay]
  );

  const getTimeAdornment = useCallback(() => {
    const labourCostType = product.material.labourCostType;
    if (labourCostType === ProductLabourCostType.FULL_DAY) {
      return dayEndAdornment;
    }
    if (labourCostType === ProductLabourCostType.HALF_DAY) {
      return halfDayEndAdornment;
    }
    return timeEndAdornment;
  }, [product.material.labourCostType]);

  const onAdd = useCallback(
    async (crewIds, setCrewIds) => {
      // TODO: Find a way to update the proposal data without calling the submit
      await handleSubmit(onSubmit)();

      const result = await addProposalCrews({
        variables: { crewIds, proposalProductId: product.id },
      });

      const success = isSuccess(result.data);
      setCrewIds([]);
      // if (success) {
      //   writeProposal(result.data.addProposalCrew as Proposal);
      // }
    },
    [addProposalCrews]
  );

  // const total = crews.reduce(
  //   (acc, val) =>
  //     acc +
  //     ((val.timeOnjob
  //       ? val.timeOnjob / 3600
  //       : crewsById[val.id]?.timeOnjob
  //       ? crewsById[val.id]?.timeOnjob / 3600
  //       : 0) +
  //       (val.timeTravel
  //         ? val.timeTravel / 3600
  //         : crewsById[val.id]?.timeTravel
  //         ? crewsById[val.id].timeTravel / 3600
  //         : 0)) *
  //       (val.rate ?? crewsById[val.id]?.rate ?? 0),
  //   0
  // );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <TableContainer component={Paper}>
          <Table sx={{ minWidth: 650 }} aria-label="simple table">
            <TableHead sx={{ display: "table-header-group" }}>
              <TableRow>
                <TableCell sx={{ width: "20%" }}>Name</TableCell>
                <TableCell align="center" sx={{ width: "10%" }}>
                  Quantity
                </TableCell>
                <TableCell align="center" sx={{ width: "16%" }}>
                  Rate
                </TableCell>
                <TableCell align="center" sx={{ width: "16%" }}>
                  Travel Time
                </TableCell>
                <TableCell align="center" sx={{ width: "16%" }}>
                  {product.material.labourCostType === ProductLabourCostType.PER_MAN_HOUR
                    ? "On Job Time"
                    : "Job Time"}
                </TableCell>
                <TableCell align="center" sx={{ width: "22%" }}>
                  Total Cost
                </TableCell>
                {!inViewMode && <TableCell align="center"></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {fields.map((labourCost, i) => {
                return (
                  <TableRow key={i} sx={{ "&:last-child td, &:last-child th": { border: 5 } }}>
                    <TableCell component="th" scope="row">
                      {crewsById[labourCost.id]?.name}
                    </TableCell>
                    <TableCell align="right">
                      <FormField
                        placeholder={product.crews?.[i]?.quantity?.toFixed(2)}
                        disabled={inViewMode}
                        align="right"
                        mb={0}
                        sx={{
                          "& .MuiInputBase-root": {
                            "& input": {
                              textAlign: "right",
                            },
                          },
                        }}
                        type="number"
                        inputProps={{ min: 1, step: 1 }}
                        {...register(`crews.${i}.quantity`, { valueAsNumber: true })}
                      />
                    </TableCell>
                    <TableCell align="right">
                      <CurrencyFormField
                        placeholder={(product.crews?.[i]?.rate / 100)?.toFixed(2)}
                        disabled={inViewMode}
                        mb={0}
                        sx={{
                          "& .MuiInputBase-root": {
                            "& input": {
                              textAlign: "right",
                            },
                          },
                        }}
                        {...currencyEndAdornment}
                        control={control}
                        name={`crews.${i}.rate`}
                        sendEmptyValuesAsNull
                        key={isSubmitSuccessful}
                      />
                    </TableCell>
                    {product.material.labourCostType === ProductLabourCostType.PER_MAN_HOUR ? (
                      <>
                        <TableCell align="right">
                          <FormField
                            placeholder={(product.crews?.[i]?.timeTravel / 3600)?.toFixed(2)}
                            disabled={inViewMode}
                            align="right"
                            mb={0}
                            sx={{
                              "& .MuiInputBase-root": {
                                "& input": {
                                  textAlign: "right",
                                },
                              },
                            }}
                            {...timeEndAdornment}
                            {...register(`crews.${i}.timeTravel`, { valueAsNumber: true })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <FormField
                            placeholder={getJobTimePlaceholder(i)}
                            disabled={inViewMode}
                            align="right"
                            mb={0}
                            sx={{
                              "& .MuiInputBase-root": {
                                "& input": {
                                  textAlign: "right",
                                },
                              },
                            }}
                            {...timeEndAdornment}
                            {...register(`crews.${i}.timeOnjob`, { valueAsNumber: true })}
                          />
                        </TableCell>
                      </>
                    ) : (
                      <>
                        <TableCell align="right">
                          <FormField
                            placeholder={"0"}
                            disabled={true}
                            align="right"
                            mb={0}
                            sx={{
                              "& .MuiInputBase-root": {
                                "& input": {
                                  textAlign: "right",
                                },
                              },
                            }}
                            {...timeEndAdornment}
                            {...register(`crews.${i}.timeTravel`, { valueAsNumber: true })}
                          />
                        </TableCell>
                        <TableCell align="right">
                          <FormField
                            placeholder={getJobTimePlaceholder(i)}
                            disabled={
                              inViewMode ||
                              (product.material.labourCostType !== ProductLabourCostType.FULL_DAY &&
                                product.material.labourCostType !== ProductLabourCostType.HALF_DAY)
                            }
                            align="right"
                            mb={0}
                            sx={{
                              "& .MuiInputBase-root": {
                                "& input": {
                                  textAlign: "right",
                                },
                              },
                            }}
                            {...getTimeAdornment()}
                            {...register(`crews.${i}.timeOnjob`, { valueAsNumber: true })}
                          />
                        </TableCell>
                      </>
                    )}
                    <TableCell align="center">
                      {formatCentsToUSD(product.crews?.[i]?.cost)}
                    </TableCell>
                    {!inViewMode && (
                      <TableCell>
                        <IconButton
                          onClick={() => {
                            remove(i);
                            handleSubmit(onSubmit)();
                          }}
                        >
                          <Icon>delete</Icon>
                        </IconButton>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
              {!inViewMode && (
                <TableRow sx={{ "&:last-child td, &:last-child th": { border: 0 } }}>
                  <TableCell colSpan={7}>
                    <AddLabourCost onAdd={onAdd} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Grid>
    </Grid>
  );
}

export default LabourCostsSubForm;
